<template>
  <div>
    <b-card>
      <b-row class="mb-2">
        <b-col>
          <b-button variant="primary" class="mr-1" @click="autoGenerate" :disabled="generating">
            <b-spinner v-if="generating" small class="mr-1" />
            {{ $t('features.vaccination.autoGenerate') }}
          </b-button>
          <b-button variant="outline-primary" @click="openAddModal">+ Add Custom</b-button>
        </b-col>
      </b-row>

      <!-- Loading skeleton -->
      <div v-if="loading">
        <b-skeleton width="40%" height="2rem" class="mb-2"/>
        <b-skeleton width="70%" class="mb-1"/>
        <b-skeleton width="55%" class="mb-1"/>
        <b-skeleton-table :rows="5" :columns="4" hide-header/>
      </div>

      <div v-else-if="!items.length" class="text-center py-4 text-muted">
        No vaccination schedule found. Generate one or add custom entries.
      </div>

      <b-table
        v-else
        :items="items"
        :fields="fields"
        striped
        hover
        responsive
      >
        <template #cell(status)="data">
          <b-badge :variant="statusVariant(data.value)">{{ data.value | capitalize }}</b-badge>
        </template>
        <template #cell(actions)="data">
          <b-button
            v-if="data.item.status === 'pending'"
            size="sm"
            variant="success"
            @click="markGiven(data.item.id)"
          >
            {{ $t('features.vaccination.markGiven') }}
          </b-button>
        </template>
      </b-table>
    </b-card>

    <!-- Add Custom Modal -->
    <b-modal
      v-model="showModal"
      title="Add Custom Vaccination"
      ok-title="Add"
      @ok="addCustom"
      @hidden="resetForm"
    >
      <b-form-group label="Vaccine Name">
        <b-form-input v-model="form.vaccine_name" placeholder="e.g. Ranikhet" :state="errors.vaccine_name ? false : null" />
        <small v-if="errors.vaccine_name" class="text-danger">{{ errors.vaccine_name }}</small>
      </b-form-group>
      <b-form-group label="Scheduled Day">
        <b-form-input v-model="form.scheduled_day" type="number" min="1" max="60" placeholder="Day number (1-60)" :state="errors.scheduled_day ? false : null" />
        <small v-if="errors.scheduled_day" class="text-danger">{{ errors.scheduled_day }}</small>
      </b-form-group>
      <b-form-group label="Notes">
        <b-form-input v-model="form.notes" placeholder="Optional notes" />
      </b-form-group>
    </b-modal>
  </div>
</template>

<script>
import axios from '@/libs/axios'
import {
  BCard, BRow, BCol, BTable, BButton, BBadge, BSpinner, BModal,
  BFormGroup, BFormInput,
} from 'bootstrap-vue'

export default {
  name: 'VaccinationScheduleList',
  components: {
    BCard, BRow, BCol, BTable, BButton, BBadge, BSpinner, BModal,
    BFormGroup, BFormInput,
  },
  filters: {
    capitalize(val) {
      if (!val) return ''
      return val.charAt(0).toUpperCase() + val.slice(1)
    },
  },
  data() {
    return {
      loading: false,
      generating: false,
      items: [],
      showModal: false,
      errors: {},
      form: { vaccine_name: '', scheduled_day: '', notes: '' },
      fields: [
        { key: 'scheduled_day', label: 'Day', sortable: true },
        { key: 'vaccine_name', label: 'Vaccine Name' },
        { key: 'due_date', label: 'Due Date' },
        { key: 'status', label: 'Status' },
        { key: 'given_date', label: 'Given Date' },
        { key: 'actions', label: 'Actions' },
      ],
    }
  },
  created() {
    this.fetchData()
  },
  methods: {
    async fetchData() {
      const { farmId, shedId, flockId } = this.$route.params
      this.loading = true
      try {
        const { data } = await axios.get(`web/farms/${farmId}/sheds/${shedId}/flocks/${flockId}/vaccination-schedules`)
        this.items = data.data || data.items || data || []
      } catch (e) {
        console.error(e)
        this.$bvToast.toast('Failed to load schedules', { variant: 'danger', title: 'Error' })
      } finally {
        this.loading = false
      }
    },
    async autoGenerate() {
      const { farmId, shedId, flockId } = this.$route.params
      this.generating = true
      try {
        await axios.post(`web/farms/${farmId}/sheds/${shedId}/flocks/${flockId}/vaccination-schedules/auto-generate`)
        this.$bvToast.toast('Schedule generated', { variant: 'success', title: 'Success' })
        this.fetchData()
      } catch (e) {
        console.error(e)
        this.$bvToast.toast('Failed to generate schedule', { variant: 'danger', title: 'Error' })
      } finally {
        this.generating = false
      }
    },
    openAddModal() {
      this.resetForm()
      this.showModal = true
    },
    resetForm() {
      this.form = { vaccine_name: '', scheduled_day: '', notes: '' }
      this.errors = {}
    },
    validateForm() {
      this.errors = {}
      if (!this.form.vaccine_name || this.form.vaccine_name.trim() === '') {
        this.errors.vaccine_name = 'Vaccine name is required'
      }
      const day = parseInt(this.form.scheduled_day)
      if (!this.form.scheduled_day || isNaN(day) || day < 1 || day > 60) {
        this.errors.scheduled_day = 'Scheduled day must be between 1 and 60'
      }
      return Object.keys(this.errors).length === 0
    },
    async addCustom(bvModalEvt) {
      bvModalEvt.preventDefault()
      if (!this.validateForm()) return
      const { farmId, shedId, flockId } = this.$route.params
      try {
        await axios.post(`web/farms/${farmId}/sheds/${shedId}/flocks/${flockId}/vaccination-schedules`, this.form)
        this.$bvToast.toast('Vaccination added', { variant: 'success', title: 'Success' })
        this.showModal = false
        this.fetchData()
      } catch (e) {
        console.error(e)
        this.$bvToast.toast('Failed to add vaccination', { variant: 'danger', title: 'Error' })
      }
    },
    async markGiven(id) {
      try {
        await axios.post(`web/vaccination-schedules/${id}/mark-given`)
        this.$bvToast.toast('Marked as given', { variant: 'success', title: 'Success' })
        this.fetchData()
      } catch (e) {
        console.error(e)
        this.$bvToast.toast('Failed to update', { variant: 'danger', title: 'Error' })
      }
    },
    statusVariant(status) {
      const map = { pending: 'warning', given: 'success', missed: 'danger' }
      return map[status] || 'secondary'
    },
  },
}
</script>

<style scoped></style>
